/**
 * A coluna Pagamento da tela de Faturas.
 *
 * ⛔ `invoices.paymentDate` é UM campo, e fatura de órgão público paga em mais
 * de uma vez. A FUNDETEC paga o STFC e deixa a locação esperando empenho: dois
 * pagamentos, em datas diferentes. Um campo único mostra um e esconde o outro.
 * E ele nem é confiável: o trigger que soma `invoice_payments` grava valor e
 * status, NUNCA a data, então quem grava a data é cada caminho de conciliação
 * — e em 29/07/2026 um deles não gravou (FAT202606002 e FAT202607014 apareciam
 * sem data, com pagamento registrado).
 *
 * A verdade é `invoice_payments`, um registro por pagamento. Esta coluna passa
 * a preferir o que sai de lá:
 *  - `lastPaymentDate` / `paymentsCount` na listagem, quando o backend mandar;
 *  - a lista de `GET /invoices/:id/payments`, depois que alguém abre o detalhe;
 *  - `paymentDate`, como era, só quando nenhum dos dois existe.
 */

export interface PagamentoDaFatura {
  id?: number;
  amount: number | string;
  paymentDate: string | null;
  source?: string | null;
  paymentMethod?: string | null;
}

/** Nome da origem como o financeiro reconhece, não como o código chama. */
export function rotuloDaOrigem(source: string | null | undefined): string {
  const s = String(source || '').toLowerCase();
  if (s === 'cnab_retorno') return 'Retorno do banco (CNAB)';
  if (s === 'sicredi_webhook') return 'Aviso automático do Sicredi';
  if (s === 'reconciliation' || s === 'sicredi_conciliacao') return 'Conciliação bancária';
  if (s === 'manual') return 'Registro manual';
  if (s.startsWith('backfill')) return 'Importado de sistema anterior';
  return s || 'Origem não informada';
}

/** Dia civil comparável (YYYY-MM-DD) de uma data da API, ou '' sem data. */
const diaCivil = (d: string | null | undefined): string => String(d || '').slice(0, 10);

/**
 * Quantos pagamentos e a data do ÚLTIMO. Pagamento sem data conta, mas não
 * disputa a última data.
 */
export function resumoDosPagamentos(lista: PagamentoDaFatura[] | null | undefined): {
  quantidade: number;
  ultimaData: string | null;
  total: number;
} {
  const pagamentos = Array.isArray(lista) ? lista : [];
  let ultima = '';
  let total = 0;
  for (const p of pagamentos) {
    total += Number(p?.amount) || 0;
    const dia = diaCivil(p?.paymentDate);
    if (dia > ultima) ultima = dia;
  }
  return { quantidade: pagamentos.length, ultimaData: ultima || null, total: Math.round(total * 100) / 100 };
}

/**
 * O que a coluna mostra: a data do último pagamento e quantos houve.
 * `lista` é a de `/payments`, quando já foi buscada; ela ganha de tudo.
 */
export function colunaPagamento(
  inv: { paymentDate?: string | null; lastPaymentDate?: string | null; paymentsCount?: number | null },
  lista?: PagamentoDaFatura[] | null,
): { data: string | null; quantidade: number | null } {
  if (Array.isArray(lista)) {
    const r = resumoDosPagamentos(lista);
    return { data: r.ultimaData ?? inv.paymentDate ?? null, quantidade: r.quantidade };
  }
  // Desde o backend #265 a listagem manda `paymentsCount` em TODA fatura (0 sem
  // pagamento, nunca ausente). Número presente quer dizer que a fonte certa foi
  // consultada: aí `lastPaymentDate` vale mesmo quando é null, e o
  // `paymentDate` antigo não volta a aparecer. O campo ausente fica para
  // resposta de backend anterior, que segue no `paymentDate`.
  const n = inv.paymentsCount;
  if (typeof n === 'number') {
    return { data: inv.lastPaymentDate || null, quantidade: n };
  }
  return { data: inv.paymentDate || null, quantidade: null };
}
